import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';

export interface Municipio {
  id: string;
  nome: string;
  sigla: string;
}

interface ActiveMunicipioContextValue {
  activeMunicipio: Municipio | null;
  municipios: Municipio[];
  setActiveMunicipio: (m: Municipio) => void;
  loadingMunicipios: boolean;
  isSwitching: boolean;
}

const ActiveMunicipioContext = createContext<ActiveMunicipioContextValue | null>(null);

const STORAGE_KEY = 'active_municipio_id';

export function ActiveMunicipioProvider({ children }: { children: ReactNode }) {
  const { profile, profileLoading, isSuperAdmin } = useAuth();
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [activeMunicipio, setActiveMunicipioState] = useState<Municipio | null>(null);
  const [loadingMunicipios, setLoadingMunicipios] = useState(true);
  const [isSwitching, setIsSwitching] = useState(false);

  const loadMunicipios = useCallback(async () => {
    if (profileLoading) return;
    setLoadingMunicipios(true);
    try {
      let query = supabase.from('municipios').select('id, nome, sigla').eq('ativo', true).order('nome');
      if (!isSuperAdmin && profile?.municipio_id) {
        query = query.eq('id', profile.municipio_id);
      }
      const { data } = await query;
      const list: Municipio[] = (data ?? []).map((r) => ({
        id: r.id as string,
        nome: r.nome as string,
        sigla: r.sigla as string,
      }));
      setMunicipios(list);
      const savedId = localStorage.getItem(STORAGE_KEY);
      const saved = list.find((m) => m.id === savedId);
      const profileMun = list.find((m) => m.id === profile?.municipio_id);
      const fallback = list[0] ?? null;
      setActiveMunicipioState(saved ?? profileMun ?? fallback);
    } finally {
      setLoadingMunicipios(false);
    }
  }, [profile, profileLoading, isSuperAdmin]);

  useEffect(() => {
    if (!profileLoading) {
      loadMunicipios();
    }
  }, [profileLoading, loadMunicipios]);

  const setActiveMunicipio = useCallback((m: Municipio) => {
    localStorage.setItem(STORAGE_KEY, m.id);
    setActiveMunicipioState(m);
    // Mostrar indicador de switching por 1.2s
    setIsSwitching(true);
    setTimeout(() => setIsSwitching(false), 1200);
  }, []);

  return (
    <ActiveMunicipioContext.Provider value={{ activeMunicipio, municipios, setActiveMunicipio, loadingMunicipios, isSwitching }}>
      {children}
    </ActiveMunicipioContext.Provider>
  );
}

export function useActiveMunicipio(): ActiveMunicipioContextValue {
  const ctx = useContext(ActiveMunicipioContext);
  if (!ctx) throw new Error('useActiveMunicipio must be used within ActiveMunicipioProvider');
  return ctx;
}
